import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type MutableRefObject, type RefObject } from 'react'
import { ChevronDown, ExternalLink, LogOut, Menu, PanelLeftClose, PanelLeftOpen, X, type LucideIcon } from 'lucide-react'
import { isStagingHost } from '@region13/shared-types'

// The shared dark-maroon left sidebar chrome used by all six platform apps
// (scheduler admin, coach, referee, fields, console, stats). Apps supply their
// brand, nav items, and any header/footer extras; this shell renders the frame,
// collapse behavior, identity, sign-out, the public-site link, and the collapse
// toggle. Nav parts (`SidebarNavLink`, `SidebarGroup`, `SidebarList`,
// `SidebarCluster`) are router-agnostic: a link's `render` spreads the sidebar's
// classes onto the app's own typed <Link>.

const CollapsedContext = createContext(false)

/** Collapsed state, for nav items rendered as children of <SidebarShell>. */
export function useSidebarCollapsed(): boolean {
  return useContext(CollapsedContext)
}

// Subtle white wash on active, no accent stripe.
const LINK_BASE =
  'flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-ink hover:bg-white/5 hover:text-white transition-colors'

/** className for a sidebar nav link / action, collapse-aware. */
function sidebarLinkClass(collapsed: boolean): string {
  return LINK_BASE + (collapsed ? ' justify-center px-0' : '')
}

/** activeProps to spread onto a TanStack <Link> inside the sidebar. */
const SIDEBAR_LINK_ACTIVE = {
  className: '!bg-white/10 !text-white font-medium',
  'aria-current': 'page' as const,
}

/** Inner content of a sidebar link: icon + (when expanded) label. Use it inside
 *  a `SidebarNavLink`'s `render`, or a link of your own, which takes its classes
 *  from `SidebarNavLink`. */
export function SidebarLinkContent({
  icon: Icon,
  label,
  collapsed,
  railLabel,
}: {
  icon: LucideIcon
  label: string
  collapsed: boolean
  /** Accessible name when the rail hides the label (defaults to `label`). */
  railLabel?: string
}) {
  return (
    <>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!collapsed ? (
        <span className="flex-1 truncate">{label}</span>
      ) : (
        <span className="sr-only">{railLabel ?? label}</span>
      )}
    </>
  )
}

export interface SidebarLinkRenderProps {
  className: string
  activeProps: { className: string; 'aria-current': 'page' }
  title: string | undefined
  children: ReactNode
}

/** A nav link without its router. `render` gets the sidebar's classes, active
 *  props and rail title; the app spreads them onto its own typed <Link>. */
export function SidebarNavLink({
  icon,
  label,
  railLabel,
  render,
}: {
  icon: LucideIcon
  label: string
  railLabel?: string
  render: (p: SidebarLinkRenderProps) => ReactNode
}) {
  const collapsed = useSidebarCollapsed()
  return render({
    className: sidebarLinkClass(collapsed),
    activeProps: SIDEBAR_LINK_ACTIVE,
    title: collapsed ? (railLabel ?? label) : undefined,
    children: <SidebarLinkContent icon={icon} label={label} collapsed={collapsed} railLabel={railLabel} />,
  })
}

/** A plain anchor styled as a sidebar link (for links off the app's router,
 *  like the public site). `newTab` opens it in a new window. */
export function SidebarExternalLink({
  href,
  icon,
  label,
  newTab = false,
}: {
  href: string
  icon: LucideIcon
  label: string
  newTab?: boolean
}) {
  const collapsed = useSidebarCollapsed()
  return (
    <a
      href={href}
      title={collapsed ? label : undefined}
      className={sidebarLinkClass(collapsed)}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <SidebarLinkContent icon={icon} label={label} collapsed={collapsed} />
    </a>
  )
}

/** Open state for a SidebarGroup. Follows the route: it resets to
 *  `activeInside` whenever `navKey` or `activeInside` changes. `toggle` flips
 *  it until the next navigation. */
export function useSidebarGroupOpen(activeInside: boolean, navKey: string): { open: boolean; toggle: () => void } {
  const [open, setOpen] = useState(activeInside)
  useEffect(() => {
    setOpen(activeInside)
  }, [activeInside, navKey])
  return { open, toggle: () => setOpen((o) => !o) }
}

/** A titled, collapsible nav group: a disclosure button (icon, label, chevron)
 *  over its leaves. In the icon rail the leaves render flat, with no heading. */
export function SidebarGroup({
  label,
  icon: Icon,
  open,
  onToggle,
  children,
}: {
  label: string
  icon: LucideIcon
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  const panelId = useId()
  const collapsed = useSidebarCollapsed()
  if (collapsed) return <>{children}</>
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-sidebar-ink hover:bg-white/5 hover:text-white transition-colors"
      >
        <span className="flex items-center gap-3">
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{label}</span>
        </span>
        <ChevronDown className={'h-4 w-4 shrink-0 transition-transform ' + (open ? 'rotate-180' : '')} aria-hidden="true" />
      </button>
      <div id={panelId} hidden={!open} className="ml-3 space-y-0.5 border-l border-sidebar-line pl-2">
        {children}
      </div>
    </div>
  )
}

/** A flat run of nav links. */
export function SidebarList({ children }: { children: ReactNode }) {
  return <div className="space-y-0.5">{children}</div>
}

/** The classes a SidebarCluster draws: the divider above it and its spacing. */
export const SIDEBAR_CLUSTER_CLASS = 'mt-3 space-y-0.5 border-t border-sidebar-line pt-3'

/** A run of nav links set off by a divider above it. */
export function SidebarCluster({ children }: { children: ReactNode }) {
  return <div className={SIDEBAR_CLUSTER_CLASS}>{children}</div>
}

type SignOut = { href: string } | { onClick: () => void }

function readCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const DESKTOP_QUERY = '(min-width: 768px)'
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Deployed build (commit SHA), bottom of the sidebar. Readable on the dark
 *  chrome (sidebar-ink, 11px); it was white/30 at 10px, 2.6:1. Lives here, not
 *  in its own file, because it reads the collapse context this file owns. */
export function BuildStamp({ build }: { build: string }) {
  const collapsed = useSidebarCollapsed()
  return (
    <div
      className={'pt-1.5 text-[11px] text-sidebar-ink ' + (collapsed ? 'text-center' : 'px-3')}
      title="Deployed build (commit SHA)"
    >
      {collapsed ? '' : 'build '}
      {build.slice(0, 7)}
    </div>
  )
}

/** The small gold "Staging" tag on the sidebar wordmark and the phone
 *  mastheads (referee, coach), shown only on a staging host. */
export function StagingMarker({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-block rounded bg-brand-gold px-1.5 py-0.5 text-[11px] font-semibold text-brand-dark ${className}`}
    >
      Staging
    </span>
  )
}

/** Modal phone drawer behavior: focus in on open, Tab trap, Escape closes,
 *  focus back to the opener on close (or to #main when a link was chosen or
 *  the viewport grew to md+, where the opener is hidden), body scroll locked
 *  while open. A key another handler already took, or one pressed inside a
 *  second modal opened over the drawer (the admin command palette), is left
 *  alone. */
function useDrawer(
  open: boolean,
  close: () => void,
  drawerRef: RefObject<HTMLElement | null>,
  openerRef: RefObject<HTMLElement | null>,
  focusMainOnClose: MutableRefObject<boolean>,
  returnFocusTo?: () => HTMLElement | null,
) {
  // Callers pass an inline function; a ref keeps the effect from re-running
  // (and stealing focus) on every render.
  const returnFocusRef = useRef(returnFocusTo)
  useLayoutEffect(() => {
    returnFocusRef.current = returnFocusTo
  })

  useEffect(() => {
    if (!open || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(DESKTOP_QUERY)
    const onChange = (e: MediaQueryListEvent) => {
      if (!e.matches) return
      focusMainOnClose.current = true
      close()
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [open, close, focusMainOnClose])

  useEffect(() => {
    if (!open) return
    const drawer = drawerRef.current
    drawer?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      const modal = e.target instanceof Element ? e.target.closest('[aria-modal="true"]') : null
      if (modal && modal !== drawer) return
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const items = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement
      if (e.shiftKey && (active === first || !drawer.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !drawer.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
      if (focusMainOnClose.current) document.getElementById('main')?.focus()
      else (openerRef.current ?? returnFocusRef.current?.() ?? document.getElementById('main'))?.focus()
      focusMainOnClose.current = false
    }
  }, [open, close, drawerRef, openerRef, focusMainOnClose])
}

/** The shared sidebar. Owns collapse state (persisted under `storageKey`) and
 *  provides it to nav children via context. `brand.home` wraps the wordmark in
 *  the app's typed <Link> for SPA navigation. Below md it is a masthead with a drawer
 *  (`mobile="drawer"`, the default) or nothing (`mobile="none"`). The drawer
 *  closes when a link in it is chosen, or a control marked `data-closes-drawer`
 *  (one that opens something of its own, like a command palette). */
export function SidebarShell({
  brand,
  storageKey,
  mobile = 'drawer',
  hideBelowMd = true,
  masthead = true,
  drawer,
  build,
  hostname,
  headerExtra,
  identity,
  signOut,
  footerExtra,
  children,
}: {
  brand: { logoSrc: string; title?: string; subtitle: string; home: (content: ReactNode) => ReactNode }
  storageKey: string
  mobile?: 'drawer' | 'none'
  /** @deprecated Use `mobile`. `false` keeps the sidebar visible on phones. */
  hideBelowMd?: boolean
  /** Render the phone masthead and its Menu button (default). Only meaningful
   *  with `mobile="drawer"`; `false` leaves opening to the caller via `drawer`. */
  masthead?: boolean
  /** Controlled drawer: the caller owns `open`. `returnFocusTo` names the
   *  opener when there is no masthead Menu button to return focus to. */
  drawer?: { open: boolean; onOpenChange: (open: boolean) => void; returnFocusTo?: () => HTMLElement | null }
  /** Deployed build; renders a BuildStamp after `footerExtra`. */
  build?: string
  /** Tests only; defaults to window.location.hostname. */
  hostname?: string
  headerExtra?: ReactNode
  identity?: string | null
  signOut: SignOut
  footerExtra?: ReactNode
  children: ReactNode
}) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey))
  const [internalOpen, setInternalOpen] = useState(false)
  const open = drawer ? drawer.open : internalOpen
  // Callers pass an inline onOpenChange; a ref keeps closeDrawer stable so the
  // drawer effects do not tear down and re-run on every parent render.
  const onOpenChangeRef = useRef(drawer?.onOpenChange)
  useLayoutEffect(() => {
    onOpenChangeRef.current = drawer?.onOpenChange
  })
  const setOpen = useCallback((next: boolean) => (onOpenChangeRef.current ?? setInternalOpen)(next), [])
  const drawerId = useId()
  const drawerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLButtonElement>(null)
  const focusMainOnClose = useRef(false)
  const closeDrawer = useCallback(() => setOpen(false), [setOpen])
  const hasDrawer = mobile === 'drawer' && hideBelowMd
  useDrawer(open && hasDrawer, closeDrawer, drawerRef, menuRef, focusMainOnClose, drawer?.returnFocusTo)

  const showLabels = !collapsed
  const title = brand.title ?? 'Region 13'
  const staging = isStagingHost(hostname ?? window.location.hostname)

  const toggle = () =>
    setCollapsed((c) => {
      const next = !c
      try {
        localStorage.setItem(storageKey, next ? '1' : '0')
      } catch {
        /* private mode */
      }
      return next
    })

  const brandInner = (labels: boolean) => (
    <span className="flex items-center gap-3">
      <img src={brand.logoSrc} alt="" aria-hidden="true" className="h-9 w-9 shrink-0" />
      {labels ? (
        <span className="leading-tight">
          <span className="block font-bold text-white">{title}</span>
          <span className="block text-xs text-sidebar-ink">{brand.subtitle}</span>
          {staging ? <StagingMarker className="mt-1" /> : null}
        </span>
      ) : null}
    </span>
  )
  const brandClass = 'flex items-center px-3 py-4 hover:opacity-90 transition-opacity'
  const brandNode = (labels: boolean) =>
    brand.home(
      <span className={brandClass} title={title}>
        {brandInner(labels)}
      </span>,
    )

  // Identity, sign-out (primary), app extras, build stamp. The collapse toggle
  // follows on the desktop rail only.
  const footer = (isCollapsed: boolean) => {
    const labels = !isCollapsed
    return (
      <>
        {labels && identity ? (
          <div className="truncate px-3 pb-1.5 text-xs text-sidebar-ink" title={identity}>
            {identity}
          </div>
        ) : null}
        {'href' in signOut ? (
          <a href={signOut.href} title="Sign out" className={sidebarLinkClass(isCollapsed)}>
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            {labels ? <span className="flex-1">Sign out</span> : <span className="sr-only">Sign out</span>}
          </a>
        ) : (
          <button
            type="button"
            onClick={signOut.onClick}
            title="Sign out"
            className={sidebarLinkClass(isCollapsed) + ' w-full'}
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            {labels ? <span className="flex-1 text-left">Sign out</span> : <span className="sr-only">Sign out</span>}
          </button>
        )}
        <SidebarExternalLink href="https://www.ayso13.org" icon={ExternalLink} label="Public site" newTab />
        {footerExtra}
        {build ? <BuildStamp build={build} /> : null}
      </>
    )
  }

  const asideVisibility = hideBelowMd ? 'hidden md:flex ' : 'flex '

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:shadow-lg"
      >
        Skip to content
      </a>
      {hasDrawer && masthead ? (
        <header className="sticky top-0 z-30 flex items-center gap-3 bg-sidebar px-4 py-3 text-sidebar-ink md:hidden">
          <img src={brand.logoSrc} alt="" aria-hidden="true" className="h-8 w-8 shrink-0" />
          <span className="leading-tight">
            <span className="block font-bold text-white">{title}</span>
            <span className="block text-xs text-sidebar-ink">{brand.subtitle}</span>
          </span>
          {staging ? <StagingMarker /> : null}
          <button
            ref={menuRef}
            type="button"
            aria-expanded={open}
            aria-controls={open ? drawerId : undefined}
            onClick={() => setOpen(true)}
            className="ml-auto rounded-md p-2 hover:bg-white/10"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Menu</span>
          </button>
        </header>
      ) : null}
      {hasDrawer && open ? (
        <>
          <div data-testid="drawer-backdrop" className="fixed inset-0 z-40 bg-black/40 md:hidden" aria-hidden="true" onClick={closeDrawer} />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            id={drawerId}
            className="bg-sidebar fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col text-sidebar-ink md:hidden"
            onClick={(e) => {
              const target = e.target as HTMLElement
              if (target.closest('a[href]')) {
                focusMainOnClose.current = true
                closeDrawer()
              } else if (target.closest('[data-closes-drawer]')) {
                closeDrawer()
              }
            }}
          >
            <CollapsedContext.Provider value={false}>
              <button
                type="button"
                onClick={closeDrawer}
                className="absolute right-2 top-2 rounded-md p-2 hover:bg-white/10"
              >
                <X className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">Close menu</span>
              </button>
              {brandNode(true)}
              {headerExtra}
              <nav className="flex-1 overflow-y-auto px-2.5 py-3" aria-label="Primary">
                {children}
              </nav>
              <div className="border-t border-sidebar-line px-2.5 py-2.5">{footer(false)}</div>
            </CollapsedContext.Provider>
          </div>
        </>
      ) : null}
      <CollapsedContext.Provider value={collapsed}>
        <aside
          className={
            'sticky top-0 h-screen shrink-0 flex-col bg-sidebar text-sidebar-ink transition-[width] duration-200 ' +
            asideVisibility +
            (collapsed ? 'w-16' : 'w-64')
          }
        >
          {brandNode(showLabels)}
          {headerExtra}

          <nav className="flex-1 overflow-y-auto px-2.5 py-3" aria-label="Primary">
            {children}
          </nav>

          <div className="border-t border-sidebar-line px-2.5 py-2.5">
            {footer(collapsed)}
            <button
              type="button"
              onClick={toggle}
              aria-expanded={!collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={
                'mt-0.5 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-ink hover:bg-white/5 hover:text-white transition-colors ' +
                (collapsed ? 'justify-center px-0' : '')
              }
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <>
                  <PanelLeftClose className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">Collapse sidebar</span>
                </>
              )}
            </button>
          </div>
        </aside>
      </CollapsedContext.Provider>
    </>
  )
}
